import React from 'react';
import {
    Instagram,
    Facebook,
    Linkedin,
    Youtube,
} from 'lucide-react';
import { MusicalNoteIcon } from '@heroicons/react/24/outline';

/* ------------------------------------------------------------------
 * Types
 * ---------------------------------------------------------------- */

export type SocialPlatform =
    | 'instagram'
    | 'tiktok'
    | 'facebook'
    | 'linkedin'
    | 'youtube';

export type SocialProfile = {
    platform: SocialPlatform;
    href: string;
    handle?: string;
    postsHref?: string;
};

export type SocialLinksProps = {
    title?: string;
    profiles: SocialProfile[];
    dark?: boolean;
};

/* ------------------------------------------------------------------
 * Icon whitelist
 * ---------------------------------------------------------------- */

const ICON_MAP: Record<SocialPlatform, React.ElementType> = {
    instagram: Instagram,
    tiktok: MusicalNoteIcon,
    facebook: Facebook,
    linkedin: Linkedin,
    youtube: Youtube,
};

/* ------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------- */

const SocialLinks = ({ title, profiles, dark }: SocialLinksProps) => {
    if (!profiles?.length) return null;

    const titleClass = dark ? 'text-white' : 'text-gray-900';

    const linkClass = dark
        ? 'text-gray-200'
        : 'text-gray-700';

    const secondaryLinkClass = dark
        ? 'text-gray-300'
        : 'text-gray-500';

    const hoverClass = dark
        ? 'hover:text-white'
        : 'hover:text-gray-900';

    return (
        <aside
            className={`rounded-lg p-4 ${
                dark ? 'bg-gray-900' : 'bg-gray-50'
            }`}
        >
            {!!title && (
                <h6
                    className={`mb-3 text-sm font-semibold uppercase tracking-wide ${titleClass}`}
                >
                    {title}
                </h6>
            )}

            <ul className="space-y-3">
                {profiles.map(
                    ({ platform, href, handle, postsHref }) => {
                        const Icon = ICON_MAP[platform];

                        return (
                            <li
                                key={platform}
                                className="flex flex-col gap-1"
                            >
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 text-sm ${linkClass} ${hoverClass}`}
                                >
                                    <Icon className="h-4 w-4 flex-shrink-0 opacity-80" />
                                    <span className="capitalize">
                                        {platform}
                                    </span>
                                    {!!handle && (
                                        <span className="opacity-70">
                                            @{handle}
                                        </span>
                                    )}
                                </a>

                                {!!postsHref && (
                                    <a
                                        href={postsHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`ml-6 text-xs underline ${secondaryLinkClass} ${hoverClass}`}
                                    >
                                        View posts
                                    </a>
                                )}
                            </li>
                        );
                    }
                )}
            </ul>
        </aside>
    );
};

export default SocialLinks;
